import { z } from 'zod';
import { toScaled } from './money.ts';
import { decimalString } from './schemas.ts';

/**
 * Entrada de notas de fornecedor (ADR 0081, C9; ADR 0072, P2): um serviço só, de Contas a Pagar,
 * com dois canais (portal e BackOffice). Aqui ficam as situações, os códigos das conferências, a
 * leitura do XML e a conversão para as 7 situações que o fornecedor vê.
 */

export const INVOICE_STATUSES = [
  'received',
  'in_review',
  'pending',
  'rejected',
  'converted',
  // Divergência de valor (D1): acima do saldo do pedido, esperando a análise; e o erro é do pedido,
  // esperando o pedido corrigido. O fornecedor vê as duas como "em conferência".
  'divergent',
  'awaiting_order',
] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/** Fila "Divergência de valor" (D1): esperando a análise ou o pedido corrigido. */
export const INVOICE_DIVERGENCE_STATUSES = ['divergent', 'awaiting_order'] as const;

/** Recortes da lista de notas que os cartões da Visão geral abrem (ADR 0097). */
export const INVOICE_INDICATORS = ['divergence', 'pending'] as const;
export type InvoiceIndicator = (typeof INVOICE_INDICATORS)[number];
export const invoiceIndicator = z.enum(INVOICE_INDICATORS);

export const INVOICE_CHANNELS = ['backoffice', 'portal'] as const;
export type InvoiceChannel = (typeof INVOICE_CHANNELS)[number];

export const INVOICE_DOCUMENT_KINDS = ['nfe', 'nfse', 'invoice'] as const;
export type InvoiceDocumentKind = (typeof INVOICE_DOCUMENT_KINDS)[number];

/** Como a nota chegou quando é lançada pela Loot em nome do fornecedor. */
export const INVOICE_ARRIVAL_MEANS = ['email', 'paper', 'other'] as const;
export type InvoiceArrivalMeans = (typeof INVOICE_ARRIVAL_MEANS)[number];

/** Arquivos: o XML é lido como dado, nunca executado; o PDF é conferido pelo conteúdo. */
export const INVOICE_XML_MAX_BYTES = 1024 * 1024;
export const INVOICE_PDF_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Conferências automáticas na entrada. Todas recusam a nota: nada fica gravado e quem enviou
 * corrige. Os textos para o fornecedor ficam no portal (pt-BR e en); os internos, nas traduções.
 */
export const INVOICE_ISSUES = [
  'xml_required',
  'xml_invalid',
  'xml_too_large',
  'pdf_required',
  'pdf_invalid',
  'file_too_large',
  'issuer_mismatch',
  'recipient_mismatch',
  'recipient_not_homologated',
  'legal_entity_not_linked',
  'duplicate_access_key',
  'duplicate_document',
  'amount_out_of_tolerance',
  'reference_required',
  'reference_not_found',
  'currency_mismatch',
  'invoice_not_allowed',
  'issue_date_invalid',
] as const;
export type InvoiceIssue = (typeof INVOICE_ISSUES)[number];

/** Data AAAA-MM-DD que existe no calendário (31/02 não passa). */
export const isRealDate = (v: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) &&
  new Date(`${v}T00:00:00Z`).toISOString().startsWith(v);
const isoDate = z.string().refine(isRealDate, 'Data inválida');

/** Dados da nota lançada pela tela interna (os arquivos vêm à parte, no mesmo envio). */
export const invoiceIntakeInput = z.object({
  legalEntityId: z.uuid(),
  partnerId: z.uuid(),
  documentKind: z.enum(INVOICE_DOCUMENT_KINDS),
  reference: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('purchase_order'), purchaseOrderId: z.uuid() }),
    z.object({ kind: z.literal('contract'), contractId: z.uuid() }),
    z.object({ kind: z.literal('none') }),
  ]),
  number: z.string().trim().min(1).max(60),
  series: z
    .string()
    .trim()
    .max(10)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  issueDate: isoDate,
  currency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Moeda inválida'),
  grossAmount: decimalString(15, 4),
  accessKey: z
    .string()
    .trim()
    .regex(/^(\d{44}|\d{50})$/, 'Chave de acesso com 44 (NF-e) ou 50 (NFS-e) dígitos')
    .nullable()
    .optional(),
  note: z
    .string()
    .trim()
    .max(500)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  /** Nº de Registro do NetSuite da nota (ADR 0073): opcional, só referência, sem integração. */
  netsuiteRecordNumber: z
    .string()
    .trim()
    .max(100)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  /** Só no lançamento pela Loot. */
  arrivalMeans: z.enum(INVOICE_ARRIVAL_MEANS),
  arrivalNote: z
    .string()
    .trim()
    .max(500)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
});
export type InvoiceIntakeInput = z.infer<typeof invoiceIntakeInput>;

/** Motivo escrito para o fornecedor (pendência e recusa): aparece como está no portal. */
export const invoiceReasonInput = z.object({ reason: z.string().trim().min(3).max(1000) });

/**
 * Decisões da análise da divergência de valor (D1): erro da nota (pendência ao fornecedor, com o
 * motivo para ele), erro do pedido (Compras corrige; motivo interno), recusar, ou religar a nota ao
 * pedido corrigido (ou ao mesmo, para conferir de novo).
 */
export const INVOICE_DIVERGENCE_DECISIONS = [
  'invoice_error',
  'order_error',
  'reject',
  'relink',
] as const;
export type InvoiceDivergenceDecision = (typeof INVOICE_DIVERGENCE_DECISIONS)[number];

export const invoiceDivergenceInput = z
  .object({
    decision: z.enum(INVOICE_DIVERGENCE_DECISIONS),
    reason: z.string().trim().min(3).max(1000),
    orderId: z.uuid().nullable().optional(),
  })
  .refine((v) => v.decision === 'relink' || v.orderId == null, {
    message: 'Pedido só ao religar',
    path: ['orderId'],
  });
export type InvoiceDivergenceInput = z.infer<typeof invoiceDivergenceInput>;

// ----- Leitura do XML -----

/** Retenção que a nota declara (o que o emitente informou; quem lança confere). */
export interface InvoiceXmlWithholding {
  taxCode: 'IRRF' | 'ISS' | 'INSS' | 'PIS' | 'COFINS' | 'CSLL' | 'PCC';
  baseAmount: string | null;
  rate: string | null;
  amount: string;
}

/** Forma de pagamento declarada na NF-e (grupo pag). */
export type InvoiceXmlPaymentMeans =
  'cash' | 'credit_card' | 'debit_card' | 'boleto' | 'pix' | 'other';

export interface InvoiceXmlData {
  kind: 'nfe' | 'nfse';
  accessKey: string | null;
  number: string | null;
  series: string | null;
  issueDate: string | null;
  issuerTaxId: string | null;
  issuerName: string | null;
  recipientTaxId: string | null;
  grossAmount: string | null;
  /** Mês da competência (AAAA-MM): o do serviço na NFS-e, o da emissão na NF-e. */
  competence: string | null;
  /** Valor líquido declarado: o que sobra depois das retenções. */
  netAmount: string | null;
  withholdings: InvoiceXmlWithholding[];
  /** Duplicatas da NF-e (vencimento e valor). */
  installments: { dueDate: string; amount: string }[];
  paymentMeans: InvoiceXmlPaymentMeans | null;
}

const entities: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};
const decode = (s: string) =>
  s.replace(/&(amp|lt|gt|quot|apos);/g, (_, e: string) => entities[e]!).trim();

/** Trecho de dentro do primeiro elemento com o nome (com ou sem prefixo de namespace). */
function section(xml: string | null, tag: string): string | null {
  if (xml == null) return null;
  const open = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${tag}(?:\\s[^>]*)?>`);
  const m = open.exec(xml);
  if (!m) return null;
  const rest = xml.slice(m.index + m[0].length);
  const close = new RegExp(`</(?:[A-Za-z_][\\w.-]*:)?${tag}>`);
  const c = close.exec(rest);
  return c ? rest.slice(0, c.index) : null;
}

/** Texto de um elemento simples, procurado dentro de `scope`. */
function text(scope: string | null, tag: string): string | null {
  if (scope == null) return null;
  const inner = section(scope, tag);
  if (inner == null || inner.includes('<')) return null;
  const v = decode(inner);
  return v === '' ? null : v;
}

function attr(xml: string, tag: string, name: string): string | null {
  const m = new RegExp(`<(?:[A-Za-z_][\\w.-]*:)?${tag}\\s[^>]*\\b${name}="([^"]*)"`).exec(xml);
  return m ? decode(m[1]!) : null;
}

const taxIdOf = (scope: string | null) => text(scope, 'CNPJ') ?? text(scope, 'CPF');

/** O texto sem o primeiro elemento com o nome (para ler só o nível de cima). */
function without(xml: string | null, tag: string): string | null {
  if (xml == null) return null;
  const re = new RegExp(
    `<(?:[A-Za-z_][\\w.-]*:)?${tag}(?:\\s[^>]*)?>[\\s\\S]*?</(?:[A-Za-z_][\\w.-]*:)?${tag}>`,
  );
  return xml.replace(re, '');
}

/** Todos os trechos com o nome, na ordem. */
function sections(xml: string | null, tag: string): string[] {
  if (xml == null) return [];
  const re = new RegExp(
    `<(?:[A-Za-z_][\\w.-]*:)?${tag}(?:\\s[^>]*)?>([\\s\\S]*?)</(?:[A-Za-z_][\\w.-]*:)?${tag}>`,
    'g',
  );
  return [...xml.matchAll(re)].map((m) => m[1]!);
}

const positive = (v: string | null) => (v && toScaled(v) > 0n ? v : null);

/** Alíquota em % com 2 casas, a partir do valor e da base (nula sem base). */
function rateOf(amount: string, base: string | null): string | null {
  if (!base || toScaled(base) <= 0n) return null;
  const hundredths = (toScaled(amount) * 10_000n * 2n + toScaled(base)) / (toScaled(base) * 2n);
  return `${hundredths / 100n}.${String(hundredths % 100n).padStart(2, '0')}`;
}

const sumOf = (values: string[]) => values.reduce((s, v) => s + toScaled(v), 0n);
const CENT_TOLERANCE = 200n; // 2 centavos, em 4 casas: arredondamentos do emissor.

const PAYMENT_MEANS: Record<string, InvoiceXmlPaymentMeans> = {
  '01': 'cash',
  '03': 'credit_card',
  '04': 'debit_card',
  '15': 'boleto',
  '17': 'pix',
};

/**
 * Retenções da NFS-e nacional. Valem os campos de valor retido e o ISS marcado como retido; PIS e
 * COFINS próprios do prestador só entram se o líquido da nota mostrar que foram descontados.
 * Emissores que somam PIS, COFINS e CSLL no campo da CSLL (retenção dos três) viram PCC.
 */
function nfseWithholdings(
  top: string | null,
  trib: string | null,
  gross: string | null,
  net: string | null,
): InvoiceXmlWithholding[] {
  const out: InvoiceXmlWithholding[] = [];
  const tribMun = section(trib, 'tribMun');
  const tribFed = section(trib, 'tribFed');
  const piscofins = section(tribFed, 'piscofins');
  const issRet = text(tribMun, 'tpRetISSQN');
  const iss = positive(amountOf(text(top, 'vISSQN')));
  if (iss && (issRet === '2' || issRet === '3')) {
    const base = amountOf(text(top, 'vBC')) ?? gross;
    out.push({
      taxCode: 'ISS',
      baseAmount: base,
      rate: amountOf(text(top, 'pAliqAplic')) ?? rateOf(iss, base),
      amount: iss,
    });
  }
  const federal = (taxCode: InvoiceXmlWithholding['taxCode'], tag: string) => {
    const v = positive(amountOf(text(tribFed, tag)));
    if (v) out.push({ taxCode, baseAmount: gross, rate: rateOf(v, gross), amount: v });
  };
  federal('IRRF', 'vRetIRRF');
  federal('INSS', 'vRetCP');
  const pis = positive(amountOf(text(piscofins, 'vPis')));
  const cofins = positive(amountOf(text(piscofins, 'vCofins')));
  const csll = positive(amountOf(text(tribFed, 'vRetCSLL')));
  if (csll) {
    const all = text(piscofins, 'tpRetPisCofins') === '3' && !pis && !cofins;
    out.push({
      taxCode: all ? 'PCC' : 'CSLL',
      baseAmount: gross,
      rate: rateOf(csll, gross),
      amount: csll,
    });
  }
  if (pis && cofins && gross && net) {
    const deducted = toScaled(gross) - toScaled(net);
    const rest = deducted - sumOf(out.map((w) => w.amount));
    const both = toScaled(pis) + toScaled(cofins);
    if (rest - both <= CENT_TOLERANCE && both - rest <= CENT_TOLERANCE) {
      const base = amountOf(text(piscofins, 'vBCPisCofins')) ?? gross;
      out.push({ taxCode: 'PIS', baseAmount: base, rate: rateOf(pis, base), amount: pis });
      out.push({ taxCode: 'COFINS', baseAmount: base, rate: rateOf(cofins, base), amount: cofins });
    }
  }
  return out;
}

/** Retenções declaradas na NF-e (grupo retTrib do total). */
function nfeWithholdings(total: string | null, gross: string | null): InvoiceXmlWithholding[] {
  const ret = section(total, 'retTrib');
  const out: InvoiceXmlWithholding[] = [];
  const add = (taxCode: InvoiceXmlWithholding['taxCode'], tag: string, baseTag?: string) => {
    const v = positive(amountOf(text(ret, tag)));
    if (!v) return;
    const base = (baseTag && positive(amountOf(text(ret, baseTag)))) || gross;
    out.push({ taxCode, baseAmount: base, rate: rateOf(v, base), amount: v });
  };
  add('PIS', 'vRetPIS');
  add('COFINS', 'vRetCOFINS');
  add('CSLL', 'vRetCSLL');
  add('IRRF', 'vIRRF', 'vBCIRRF');
  add('INSS', 'vRetPrev', 'vBCRetPrev');
  return out;
}
const dateOf = (v: string | null) => (v && isRealDate(v.slice(0, 10)) ? v.slice(0, 10) : null);
const amountOf = (v: string | null) => (v && /^\d{1,15}(\.\d{1,4})?$/.test(v) ? v : null);

/**
 * Lê os dados da NF-e (modelo 55) ou da NFS-e do padrão nacional. O XML é tratado só como texto:
 * nada é baixado, executado ou expandido (DOCTYPE e entidades próprias são recusados). Devolve
 * `null` quando o arquivo não é um desses dois layouts; a NFS-e de outros layouts é digitada.
 */
export function readInvoiceXml(xml: string): InvoiceXmlData | null {
  if (xml.length > INVOICE_XML_MAX_BYTES || /<!DOCTYPE|<!ENTITY/i.test(xml)) return null;
  const clean = xml.replace(/<!--[\s\S]*?-->/g, '');
  const nfe = section(clean, 'infNFe');
  if (nfe != null) {
    const id = attr(clean, 'infNFe', 'Id');
    const ide = section(nfe, 'ide');
    const emit = section(nfe, 'emit');
    const total = section(nfe, 'total');
    const gross = amountOf(text(section(total, 'ICMSTot'), 'vNF'));
    const issueDate = dateOf(text(ide, 'dhEmi') ?? text(ide, 'dEmi'));
    const tPag = text(section(section(nfe, 'pag'), 'detPag'), 'tPag');
    return {
      kind: 'nfe',
      accessKey: id && /^NFe\d{44}$/.test(id) ? id.slice(3) : null,
      number: text(ide, 'nNF'),
      series: text(ide, 'serie'),
      issueDate,
      issuerTaxId: taxIdOf(emit),
      issuerName: text(emit, 'xNome'),
      recipientTaxId: taxIdOf(section(nfe, 'dest')),
      grossAmount: gross,
      competence: issueDate?.slice(0, 7) ?? null,
      netAmount: amountOf(text(section(section(nfe, 'cobr'), 'fat'), 'vLiq')) ?? gross,
      withholdings: nfeWithholdings(total, gross),
      installments: sections(section(nfe, 'cobr'), 'dup').flatMap((d) => {
        const dueDate = dateOf(text(d, 'dVenc'));
        const amount = positive(amountOf(text(d, 'vDup')));
        return dueDate && amount ? [{ dueDate, amount }] : [];
      }),
      paymentMeans: tPag ? (PAYMENT_MEANS[tPag] ?? 'other') : null,
    };
  }
  const nfse = section(clean, 'infNFSe');
  if (nfse != null) {
    const id = attr(clean, 'infNFSe', 'Id');
    const dps = section(nfse, 'infDPS');
    const values = section(dps ?? nfse, 'valores');
    // Valores apurados da nota, no nível de cima (fora da DPS e do grupo do IBS e da CBS).
    const top = section(without(without(nfse, 'DPS'), 'IBSCBS'), 'valores');
    const gross = amountOf(text(section(values, 'vServPrest'), 'vServ'));
    const net = amountOf(text(top, 'vLiq'));
    const competence = dateOf(text(dps, 'dCompet'));
    const issueDate = dateOf(text(dps, 'dhEmi') ?? text(nfse, 'dhProc'));
    return {
      kind: 'nfse',
      accessKey: id && /^NFS\d{50}$/.test(id) ? id.slice(3) : null,
      number: text(nfse, 'nNFSe'),
      series: text(dps, 'serie'),
      issueDate,
      issuerTaxId: taxIdOf(section(nfse, 'emit')) ?? taxIdOf(section(dps, 'prest')),
      issuerName: text(section(nfse, 'emit'), 'xNome'),
      recipientTaxId: taxIdOf(section(dps, 'toma')),
      grossAmount: gross,
      competence: (competence ?? issueDate)?.slice(0, 7) ?? null,
      netAmount: net,
      withholdings: nfseWithholdings(top, section(values, 'trib'), gross, net),
      installments: [],
      paymentMeans: null,
    };
  }
  return null;
}

// ----- Situação que o fornecedor vê (ADR 0072, seção 4) -----

export type SupplierInvoiceStatus =
  'received' | 'in_review' | 'pending' | 'approved' | 'scheduled' | 'paid' | 'rejected';

/**
 * Situação da nota para o fornecedor: antes dos títulos, a da própria nota (a divergência de
 * valor aparece como "em conferência", sem o motivo interno); depois, a dos títulos
 * vivos (nenhum = recusada; algum em rascunho ou aprovação = em conferência; todos pagos = paga;
 * alguma parcela programada = pagamento programado; senão, aprovada para pagamento).
 */
export function supplierInvoiceStatus(
  invoice: InvoiceStatus,
  payables: { status: string; anyScheduled: boolean }[],
): SupplierInvoiceStatus {
  if (invoice === 'divergent' || invoice === 'awaiting_order') return 'in_review';
  if (invoice !== 'converted') return invoice;
  const live = payables.filter((t) => t.status !== 'cancelled' && t.status !== 'rejected');
  if (live.length === 0) return 'rejected';
  if (live.some((t) => t.status === 'draft' || t.status === 'in_approval')) return 'in_review';
  if (live.every((t) => t.status === 'paid')) return 'paid';
  return live.some((t) => t.anyScheduled) ? 'scheduled' : 'approved';
}
